import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Share2, MessageCircle, Sparkles, Moon, Download, AlertTriangle, TrendingUp, ScrollText, MessageSquare, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { generateReportPDF } from "@/lib/pdf";

const STARS = Array.from({ length: 35 }, () => {
  const top = Math.random() * 100;
  const left = Math.random() * 100;
  return {
    id: `r-${top.toFixed(3)}-${left.toFixed(3)}`,
    top, left,
    size: Math.random() * 2.2 + 1, delay: Math.random() * 4, duration: 3 + Math.random() * 4,
  };
});

function StarField() {
  return (
    <div className="starfield" aria-hidden>
      {STARS.map((s) => <span key={s.id} className="star" style={{
        top: `${s.top}%`, left: `${s.left}%`, width: `${s.size}px`, height: `${s.size}px`,
        animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s`,
      }} />)}
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [kundali, setKundali] = useState(null);
  const [kundaliErr, setKundaliErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  // Fetch report when route param `id` changes. `api` is a stable ES module import;
  // setters (`setReport`, `setKundali`, etc.) are stable React refs.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/matches/${id}/report`);
        setReport(data);
        api.get(`/matches/${id}/kundali`)
          .then((r) => setKundali(r.data))
          .catch((e) => setKundaliErr(e?.response?.data?.detail || "Kundali fusion unavailable"));
      } catch (err) {
        if (err?.response?.status === 402) setLocked(true);
        else toast.error("Failed to load report");
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      {/* Celestial skeleton hero */}
      <div className="relative overflow-hidden" style={{ background: "#12103A", color: "white" }}>
        <StarField />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 relative">
          <div className="devanagari text-amber-300 text-sm">अनुबंध बुद्धि प्रतिवेदन</div>
          <div className="overline mt-2" style={{ color: "#FCD34D" }}>Reading the stars…</div>
          <div className="mt-3 h-10 w-72 rounded-lg bg-white/10 animate-pulse" />
          <div className="mt-3 h-4 w-56 rounded bg-white/5 animate-pulse" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={`sk-hero-${i}`} className="border border-white/10 rounded-2xl p-5 bg-white/5">
                <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
                <div className="mt-3 h-10 w-20 rounded bg-white/10 animate-pulse" />
                <div className="mt-2 h-3 w-32 rounded bg-white/5 animate-pulse" />
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3 text-xs text-stone-400">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span>Anubandh Intelligence is composing your Marriage Intelligence Report — about 10 seconds.</span>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-stone-200 rounded-2xl bg-white p-8">
          <div className="h-3 w-32 rounded bg-stone-200 animate-pulse" />
          <div className="mt-4 h-7 w-3/4 rounded bg-stone-200 animate-pulse" />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={`sk-block-${i}`}>
                <div className="h-3 w-28 rounded bg-stone-200 animate-pulse" />
                <div className="mt-3 space-y-2">
                  {[0, 1, 2].map((j) => <div key={j} className="h-3 rounded bg-stone-100 animate-pulse" style={{ width: `${70 + (j * 5)}%` }} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="border border-stone-200 rounded-2xl bg-white p-4">
            <div className="h-3 w-32 rounded bg-stone-200 animate-pulse" />
            <div className="mt-4 h-64 rounded bg-stone-100 animate-pulse" />
          </div>
          <div className="rounded-2xl p-6 text-white" style={{ background: "#12103A" }}>
            <div className="h-3 w-32 rounded bg-white/10 animate-pulse" />
            <div className="mt-4 h-6 w-40 rounded bg-white/10 animate-pulse" />
            <div className="mt-4 space-y-2">
              {[0, 1, 2].map((i) => <div key={i} className="h-2 rounded bg-white/10 animate-pulse" />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (locked) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]"><Nav />
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="overline">Locked</div>
          <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Unlock the full Report.</h1>
          <p className="mt-4 text-stone-600">The Marriage Intelligence Report — with Kundali × AI Fusion, AI explainability, and family-shareable summaries — is available on Clarity (Premium) and above.</p>
          <Link to="/subscription" data-testid="report-upgrade-cta" className="btn-pill inline-flex mt-8 text-white" style={{ background: "#1E1B4B" }}>Upgrade to Clarity</Link>
        </div>
      </div>
    );
  }

  if (!report) return null;
  const shareUrl = `${window.location.origin}/report/share/${report.share_id}`;
  const copyShare = async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied"); };
  const downloadPDF = () => {
    try {
      generateReportPDF(report, kundali);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to generate PDF");
    }
  };
  const radarData = Object.entries(report.dimension_scores).map(([k, v]) => ({ dim: k, score: v }));
  const dealBreakers = report.ai?.deal_breakers || [];
  const growth = report.ai?.growth_trajectory;
  const firstConv = report.ai?.first_conversation || [];
  const elderLetter = report.ai?.elder_letter;
  const chem = report.ai?.chemistry_scores;
  const confidence = typeof report.confidence === "number" ? report.confidence : null;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />

      {/* Celestial hero */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
        className="relative overflow-hidden" style={{ background: "#12103A", color: "white" }}
      >
        <StarField />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 relative">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="devanagari text-amber-300 text-sm">अनुबंध बुद्धि प्रतिवेदन</div>
              <div className="overline mt-2" style={{ color: "#FCD34D" }}>Marriage Intelligence Report</div>
              <h1 className="font-serif-display text-4xl lg:text-6xl mt-3 leading-tight">{report.name}</h1>
              <p className="mt-3 text-stone-300">{report.profile.profession} · {report.profile.city}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="report-share-btn" onClick={copyShare} className="btn-pill border border-white/20 text-white text-sm flex items-center gap-2 hover:bg-white/10"><Share2 size={14} /> Share</button>
              <button onClick={downloadPDF} className="btn-pill border border-white/20 text-white text-sm flex items-center gap-2 hover:bg-white/10" data-testid="report-download-btn"><Download size={14} /> PDF</button>
              <button data-testid="report-chat-btn" onClick={() => nav(`/chat/${id}`)} className="btn-pill bg-amber-500 text-[#12103A] text-sm flex items-center gap-2"><MessageCircle size={14} /> Message</button>
            </div>
          </div>
          {confidence !== null && (
            <div className="mt-6 inline-flex items-center gap-2 border border-white/15 rounded-full px-3 py-1.5 text-xs bg-white/5" data-testid="report-confidence-badge">
              <ShieldCheck size={12} className="text-amber-300" />
              <span className="text-stone-300">Score Confidence:</span>
              <span className="text-amber-300 font-medium">{confidence}%</span>
              <span className="text-stone-500">· based on your assessment coverage</span>
            </div>
          )}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-white/10 rounded-2xl p-5 bg-white/5">
              <div className="overline" style={{ color: "#FCD34D" }}>AI Alignment</div>
              <div className="font-serif-display text-4xl mt-2">{report.overall_score}%</div>
              <div className="text-xs text-stone-400 mt-1">Across {radarData.length} dimensions</div>
            </div>
            <div className="border border-white/10 rounded-2xl p-5 bg-white/5">
              <div className="overline" style={{ color: "#FCD34D" }}>Vedic Kundali</div>
              <div className="font-serif-display text-4xl mt-2">{kundali ? `${kundali.vedic.percentage}%` : "—"}</div>
              <div className="text-xs text-stone-400 mt-1">{kundali ? `${kundali.vedic.total}/36 gunas` : (kundaliErr || "Loading…")}</div>
            </div>
            <div className="border rounded-2xl p-5" style={{ background: "linear-gradient(135deg,#D97706,#FBBF24)", color: "#12103A", borderColor: "#FCD34D" }}>
              <div className="text-xs uppercase tracking-widest text-[#12103A]/80">Fusion Score</div>
              <div className="font-serif-display text-4xl mt-2">{kundali ? `${kundali.fusion_percentage}%` : `${report.overall_score}%`}</div>
              <div className="text-xs mt-1">{kundali ? kundali.fusion_verdict : "Add birth date in Profile"}</div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* AI content */}
          <div className="lg:col-span-2">
            {/* Match Percentage Breakdown — weighted (Lifestyle 60% + Professional 40%) */}
            {report.profile_match && (
              <div className="rounded-2xl p-6 bg-white border-2 border-amber-200 mb-6" data-testid="report-match-breakdown">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <div className="overline text-amber-800">Match Percentage · How we calculated it</div>
                  <div className="font-serif-display text-3xl text-[#1E1B4B]">{report.overall_score}%</div>
                </div>
                <p className="text-xs text-stone-500 mt-1">
                  Weighted from your profile compatibility, gently nudged by your Assessment agreement.
                </p>
                <div className="mt-5 space-y-4">
                  <div>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium text-[#1E1B4B] flex items-center gap-2">
                        Lifestyle &amp; Values
                        <span className="text-[10px] uppercase tracking-widest bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-2 py-0.5">60%</span>
                      </span>
                      <span className="font-serif-display text-lg text-[#1E1B4B]">{report.profile_match.lifestyle_values_score}%</span>
                    </div>
                    <div className="mt-2 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${report.profile_match.lifestyle_values_score}%` }} />
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      {Object.entries(report.profile_match.lifestyle_values_breakdown || {}).map(([k, v]) => (
                        <div key={k} className="text-[10px] flex justify-between border border-stone-100 rounded px-2 py-1 bg-stone-50">
                          <span className="text-stone-500">{k}</span>
                          <span className="text-[#1E1B4B] font-medium">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium text-[#1E1B4B] flex items-center gap-2">
                        Professional &amp; Educational
                        <span className="text-[10px] uppercase tracking-widest bg-indigo-100 border border-indigo-300 text-indigo-800 rounded-full px-2 py-0.5">40%</span>
                      </span>
                      <span className="font-serif-display text-lg text-[#1E1B4B]">{report.profile_match.professional_educational_score}%</span>
                    </div>
                    <div className="mt-2 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#1E1B4B]" style={{ width: `${report.profile_match.professional_educational_score}%` }} />
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {Object.entries(report.profile_match.professional_educational_breakdown || {}).map(([k, v]) => (
                        <div key={k} className="text-[10px] flex justify-between border border-stone-100 rounded px-2 py-1 bg-stone-50">
                          <span className="text-stone-500">{k}</span>
                          <span className="text-[#1E1B4B] font-medium">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {report.profile_match.commonalities?.length > 0 && (
                    <div className="pt-3 border-t border-stone-100">
                      <div className="overline mb-2">What you share</div>
                      <div className="flex flex-wrap gap-2">
                        {report.profile_match.commonalities.map((c) => (
                          <span key={c} className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1">
                            ✓ {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="border border-stone-200 rounded-2xl bg-white p-8">
            <div className="overline">AI Summary <Sparkles className="inline text-amber-700" size={14} /></div>
            <p className="font-serif-display text-2xl text-[#1E1B4B] mt-3 leading-snug">{report.ai.headline}</p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <div className="overline">Shared Strengths</div>
                <ul className="mt-3 space-y-2 text-sm text-stone-700">
                  {report.ai.shared_strengths.map((s) => <li key={`ss-${s}`} className="flex gap-2"><span className="text-amber-700">◆</span>{s}</li>)}
                </ul>
              </div>
              <div>
                <div className="overline">Potential Challenges</div>
                <ul className="mt-3 space-y-2 text-sm text-stone-700">
                  {report.ai.potential_challenges.map((s) => <li key={`pc-${s}`} className="flex gap-2"><span className="text-amber-700">◇</span>{s}</li>)}
                </ul>
              </div>
              <div className="sm:col-span-2">
                <div className="overline">Recommended Conversations</div>
                <ul className="mt-3 space-y-2 text-sm text-stone-700">
                  {report.ai.recommended_conversations.map((s) => <li key={`rc-${s}`} className="flex gap-2"><span className="text-amber-700">→</span>{s}</li>)}
                </ul>
              </div>
              <div className="sm:col-span-2">
                <div className="overline">Family Fit</div>
                <p className="mt-3 text-sm text-stone-700 leading-relaxed">{report.ai.family_fit}</p>
              </div>
              <div className="sm:col-span-2">
                <div className="overline">Readiness Actions</div>
                <ul className="mt-3 space-y-2 text-sm text-stone-700">
                  {report.ai.readiness_actions.map((s) => <li key={`ra-${s}`} className="flex gap-2"><span className="text-amber-700">✦</span>{s}</li>)}
                </ul>
              </div>
            </div>
            </div>{/* /AI Summary card */}

            {/* Deal-Breaker Alerts — surfaces non-negotiable conflicts */}
            {dealBreakers.length > 0 && (
              <div className="mt-8 rounded-2xl border-2 border-amber-400 bg-amber-50 p-6" data-testid="report-deal-breakers">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="text-amber-700" size={18} />
                  <div className="overline text-amber-800">Deal-Breaker Alerts</div>
                </div>
                <p className="text-sm text-stone-700 mt-2">Non-negotiable areas where alignment matters most. Best discussed early.</p>
                <ul className="mt-4 space-y-3">
                  {dealBreakers.map((d) => (
                    <li key={`db-${d}`} className="flex gap-3 text-sm text-stone-800">
                      <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs grid place-items-center shrink-0 mt-0.5">!</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Growth Trajectory */}
            {growth && (
              <div className="mt-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-6" data-testid="report-growth-trajectory">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="text-emerald-700" size={18} />
                    <div className="overline text-emerald-800">Growth Trajectory</div>
                  </div>
                  <div className="font-serif-display text-3xl text-emerald-800">{growth.score}%</div>
                </div>
                <p className="mt-3 font-serif-display text-lg text-[#1E1B4B] leading-snug">{growth.verdict}</p>
                {growth.drivers?.length > 0 && (
                  <ul className="mt-4 space-y-2 text-sm text-stone-700">
                    {growth.drivers.map((d) => <li key={`gd-${d}`} className="flex gap-2"><span className="text-emerald-700">↗</span>{d}</li>)}
                  </ul>
                )}
              </div>
            )}

            {/* First 3 Questions — personalised to this pair, not templated */}
            {firstConv.length > 0 && (
              <div className="mt-6 rounded-2xl bg-white border border-stone-200 p-6" data-testid="report-first-conversation">
                <div className="flex items-center gap-2">
                  <MessageSquare className="text-amber-700" size={18} />
                  <div className="overline">First 3 Questions</div>
                </div>
                <p className="text-sm text-stone-600 mt-2">Specific to this pair. Designed for your first meaningful conversation.</p>
                <ol className="mt-5 space-y-4">
                  {firstConv.map((q, i) => (
                    <li key={`fc-${i}`} className="flex gap-4">
                      <div className="font-serif-display text-3xl text-amber-600/70 leading-none shrink-0">{String(i + 1).padStart(2, "0")}</div>
                      <p className="text-[15px] text-[#1E1B4B] leading-relaxed pt-1">{q}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* For the Elders — respectful letter to parents */}
            {elderLetter && (
              <div className="mt-6 rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: "#12103A" }} data-testid="report-elder-letter">
                <StarField />
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <ScrollText className="text-amber-300" size={18} />
                    <div className="overline" style={{ color: "#FCD34D" }}>A note for the elders</div>
                  </div>
                  <p className="mt-4 font-serif-display text-lg leading-relaxed italic text-stone-100">
                    &ldquo;{elderLetter}&rdquo;
                  </p>
                  <div className="mt-5 text-xs text-stone-400">
                    This section is designed to be read aloud with parents. Share the PDF or link
                    from the sidebar.
                  </div>
                </div>
              </div>
            )}

            {/* Two of You — chemistry mini-scores */}
            {chem && (
              <div className="mt-6 rounded-2xl bg-white border border-stone-200 p-6" data-testid="report-chemistry">
                <div className="overline">Two of You — Chemistry</div>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { key: "intellectual", label: "Intellectual" },
                    { key: "emotional", label: "Emotional" },
                    { key: "life_rhythm", label: "Life Rhythm" },
                    { key: "values", label: "Values" },
                  ].map((c) => (
                    <div key={c.key} className="rounded-xl border border-stone-200 p-4">
                      <div className="text-xs text-stone-500 uppercase tracking-widest">{c.label}</div>
                      <div className="font-serif-display text-2xl text-[#1E1B4B] mt-1">{chem[c.key] || 0}%</div>
                      <div className="mt-2 h-1 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500" style={{ width: `${chem[c.key] || 0}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="border border-stone-200 rounded-2xl bg-white p-4">
              <div className="overline px-2 pt-2">Dimension Radar</div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E7E5E4" />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: "#57534E", fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#A8A29E", fontSize: 10 }} />
                    <Radar name="Alignment" dataKey="score" stroke="#D97706" fill="#D97706" fillOpacity={0.3} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              {/* Numbered dimensions table — Sr. No. | Dimension | Score */}
              <table className="w-full px-2 pb-2 text-sm" data-testid="report-dimensions-table">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th scope="col" className="text-left text-[10px] uppercase tracking-widest text-stone-500 py-2 w-12 pl-2">Sr. No.</th>
                    <th scope="col" className="text-left text-[10px] uppercase tracking-widest text-stone-500 py-2">Dimension</th>
                    <th scope="col" className="text-right text-[10px] uppercase tracking-widest text-stone-500 py-2 w-16 pr-2">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {radarData.map((d, i) => (
                    <tr key={d.dim} className="border-b border-stone-100 last:border-0">
                      <td className="py-2 text-stone-500 tabular-nums pl-2">{i + 1}.</td>
                      <td className="py-2 text-[#1E1B4B]">{d.dim}</td>
                      <td className="py-2 text-right font-medium text-[#1E1B4B] tabular-nums pr-2">{d.score}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: "#12103A" }}>
              <StarField />
              <div className="relative">
                <div className="flex items-center gap-2">
                  <Moon className="text-amber-300" size={18} />
                  <div className="overline" style={{ color: "#FCD34D" }}>Kundali × AI Fusion</div>
                </div>
                {kundali ? (
                  <>
                    <div className="mt-3 font-serif-display text-2xl">{kundali.vedic.verdict}</div>
                    <div className="mt-4 space-y-2">
                      {kundali.vedic.kootas.slice(0, 4).map((k) => (
                        <div key={k.name}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-stone-300">{k.name}</span>
                            <span className="text-amber-300">{k.earned}/{k.max}</span>
                          </div>
                          <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full bg-amber-400" style={{ width: `${k.pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-xs text-stone-400 border-t border-white/10 pt-3">
                      <div>{kundali.vedic.mangal_dosha}</div>
                      <div className="mt-2 italic opacity-70">{kundali.vedic.disclaimer}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="mt-3 text-sm text-stone-300">{kundaliErr || "Loading Vedic fusion…"}</p>
                    {kundaliErr && kundaliErr.toLowerCase().includes("date of birth") && (
                      <Link to="/profile" className="btn-pill mt-4 inline-flex bg-amber-500 text-[#12103A] text-sm px-4 py-2">Add birth details</Link>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="rounded-2xl p-6 bg-gradient-to-br from-amber-50 to-white border-2 border-amber-300">
              <div className="flex items-center gap-2">
                <Share2 className="text-amber-700" size={18} />
                <div className="overline text-amber-800">Family Shareable Report</div>
              </div>
              <p className="text-sm text-stone-700 mt-3 leading-relaxed">
                Send this link to a parent or elder. They can view the full Marriage Intelligence
                Report — no signup needed. Revoke anytime by regenerating.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  data-testid="report-family-copy-btn"
                  onClick={copyShare}
                  className="btn-pill w-full text-white text-sm py-2.5 flex items-center justify-center gap-2"
                  style={{ background: "#1E1B4B" }}
                >
                  <Share2 size={14} /> Copy shareable link
                </button>
                <a
                  data-testid="report-family-whatsapp-btn"
                  href={`https://wa.me/?text=${encodeURIComponent(`Please read our Marriage Intelligence Report — ${shareUrl}`)}`}
                  target="_blank" rel="noreferrer"
                  className="btn-pill w-full border border-emerald-500 text-emerald-700 text-sm py-2.5 flex items-center justify-center gap-2 hover:bg-emerald-50"
                >
                  <MessageCircle size={14} /> Share via WhatsApp
                </a>
                <button
                  data-testid="report-family-pdf-btn"
                  onClick={downloadPDF}
                  className="btn-pill w-full border border-stone-300 text-[#1E1B4B] text-sm py-2.5 flex items-center justify-center gap-2 hover:bg-stone-50"
                >
                  <Download size={14} /> Download family PDF
                </button>
              </div>
              <div className="text-[10px] text-stone-500 mt-3 break-all bg-white/60 rounded-lg p-2 border border-stone-100">
                {shareUrl}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
